import React, { useState } from "react";
import { container_classname } from "./constants";
import { GoArrowUpRight } from "react-icons/go";
import { BiDownArrowAlt } from "react-icons/bi";
import { isJSXElement } from "@utils/common";

interface FAQItem {
    question: string;
    answer: string | JSX.Element;
}

export interface FAQSectionProps {
    title: string;
    faqItems: FAQItem[];
}

const defaultFAQData: FAQSectionProps = {
    title: "Frequently asked questions",
    faqItems: [
        {
            question: "Does Gallabox charge additional fees beyond WhatsApp's conversation rates?",
            answer: (
                <div>
                    Yes, Gallabox does charge a subscription fee on top of standard WhatsApp rates. This allows us to support you with our advanced automation and chatbot
                    capabilities. For more details on the exact pricing, you could check out the{" "}
                    <a href="https://gallabox.com/pricing" className="bg-blue-400 underline">
                        Gallabox pricing page
                    </a>
                </div>
            ),
        },
        {
            question: "How can I estimate my monthly WhatsApp API costs with Gallabox?",
            answer: (
                <>
                    You can easily calculate your WhatsApp Business API costs using Gallabox’s{" "}
                    <a href="https://gallabox.com/whatsapp-conversation-pricing-calculator" className="bg-blue-400 underline">
                        WhatsApp Conversation Pricing Calculator
                    </a>
                    . The calculator takes into account your Country and region, and the conversation types you would be using the WhatsApp Business API for. Aside from the varying
                    exchange rates and BSP (Business Service Provider) fees, you should be able to get a ballpark estimate on how much your WhatsApp API charges might add up to.
                </>
            ),
        },
        {
            question: "Is WhatsApp API pricing the same across all countries?",
            answer: (
                <p>
                    No, WhatsApp API pricing varies from country to country. The conversation costs depend on the recipient’s country and is typically lower in WhatsApp-dense
                    populations such as India. It also differs in terms of the kind of message sent, which falls under four main categories: marketing, utility, service and
                    authentication. <br />
                    Are there any free tiers or trial versions available for WhatsApp API access? Yes, Gallabox offers a 7-day free trial with free message credits that you can use
                    to send messages to your customers. Effective November 1, 2024, Meta allows a free tier for WhatsApp API access, which enables you to send unlimited service
                    messages to users.
                </p>
            ),
        },
        {
            question: "Is the WhatsApp API pricing different for different use cases (e.g., marketing vs. customer support)?",
            answer: (
                <div>
                    Yes, WhatsApp API pricing varies based on use cases and type of messages sent. For example, marketing message templates usually cost more than authentication
                    templates. Utility templates cost the least whereas services messages are free since November 1, 2024.
                </div>
            ),
        },
        {
            question: "Is Gallabox's WhatsApp API pricing suitable for startups or small businesses?",
            answer: (
                <div>
                    Yes, Gallabox WhatsApp API pricing can be suitable for startups and small businesses. Since Gallabox offers tiered pricing options, businesses can choose a plan
                    based on their budget, specific requirements, and message volume. For those experimenting, there’s also a free trial available.
                </div>
            ),
        },
        {
            question: "Are there volume-based discounts available in Gallabox for large enterprises?",
            answer: (
                <>
                    Yes, Gallabox offers volume-based discounts for large teams and enterprises under its custom pricing plan. To know the specifics, you can get in touch with us
                    by filling up this{" "}
                    <a href="https://tally.so/r/nP1Wq5" className="bg-blue-400 underline">
                        form
                    </a>
                    about your requirements.
                </>
            ),
        },
        {
            question: "Are there extra costs for expanding users or channels in Gallabox?",
            answer: (
                <p>
                    Yes, Gallabox’s Growth and Scale plans include 6 users by default. You can add more users at ₹1,000/month per login or integrate additional WhatsApp channels
                    for ₹1,200/month each, allowing seamless scaling as your operations grow.
                </p>
            ),
        },
        {
            question: "How does Gallabox’s pricing differ from competitors like Wati?",
            answer: (
                <p>
                    Gallabox is more cost-effective, with plans starting at ₹799/month (unlimited conversations included). Comparatively, Wati begins at ₹1,999/month and imposes
                    usage limits. Gallabox also allocates more user logins in advanced-tier plans.
                </p>
            ),
        },
        {
            question: "Does Gallabox charge setup or recurring fees for WhatsApp API access?",
            answer: (
                <p>
                    No setup fees apply for integrations. Subscription plans cover essential WhatsApp API functionalities from the start, with pricing tailored to your business’s
                    scale and requirements.
                </p>
            ),
        },
        {
            question: "Are pre-built WhatsApp message templates part of Gallabox’s plans?",
            answer: <div>Yes, the Starter plan offers 500+ customizable templates to accelerate campaigns. You can adapt them to align with your branding and objectives.</div>,
        },
        {
            question: "How does Meta’s WhatsApp API pricing compare to Business Solution Providers (BSPs) like Gallabox?",
            answer: (
                <div>
                    Meta bills businesses per conversation (user-initiated or business-initiated). BSPs like Gallabox combine platform access, software tools, and bundled features
                    into flexible plans, streamlining WhatsApp management and enhancing functionality beyond Meta’s base API.
                </div>
            ),
        },
    ],
};

const FAQSection: React.FC<{ faqData?: FAQSectionProps }> = ({ faqData = defaultFAQData }) => {
    const { title, faqItems } = faqData;

    const [openedFaqItems, setOpenedFaqItems] = useState<number[]>([0]);
    const [showAll, setShowAll] = useState(false);

    const toggleAccordion = (index: number) => {
        setOpenedFaqItems((prevItems) => {
            if (prevItems.includes(index)) {
                return prevItems.filter((i) => i !== index);
            } else {
                return [index];
            }
        });
    };

    const handleShowAll = () => {
        setShowAll((prevShowAll) => !prevShowAll);
    };

    const visibleFAQItems = showAll ? faqItems : faqItems.slice(0, 3);

    return (
        <section className="bg-gray-100 py-20">
            <div className={`${container_classname} flex md:flex-row flex-col md:gap-x-24`}>
                <h2 className="md:text-6xl text-4xl font-semibold md:w-2/5 w-full text-left mb-6 md:mb-0 tracking-wide" style={{ fontFamily: "Degular Display" }}>
                    {title}
                </h2>
                <div className="faq-section md:w-1/2 w-full">
                    {visibleFAQItems.map((faqItems, index) => (
                        <div key={index} className={`accordion-item ${openedFaqItems.includes(index) ? "active" : null}`}>
                            <h3 className="accordion-header text-lg font-medium" onClick={() => toggleAccordion(index)}>
                                {faqItems.question}
                                <span
                                    className={`${openedFaqItems.includes(index) ? "rotated" : null}`}
                                    style={{
                                        flexShrink: 0,
                                        transition: "transform 0.4s ease",
                                    }}
                                >
                                    <GoArrowUpRight />
                                </span>
                            </h3>
                            <div className="accordion-content">{!isJSXElement(faqItems.answer) ? <p>{faqItems.answer}</p> : faqItems.answer}</div>
                        </div>
                    ))}
                    {faqItems.length > 3 && (
                        <button className="show-more flex items-center gap-x-2 text-lg" onClick={handleShowAll}>
                            {showAll ? "Less FAQ" : "More FAQ"} <BiDownArrowAlt size={22} />
                        </button>
                    )}
                </div>
            </div>
        </section>
    );
};

export default FAQSection;
